/**
 * 设置页（**N 域 N1–N3 + L 域面板一**）—— 🔴 由**顶栏齿轮**进入（`AC-O1.6`：设置入口在顶栏、不占导航位）
 *
 * ## 🔴 本页只画**服务端已支撑**的项（这是本页最重要的一条纪律）
 *
 * N 域 PRD §3.1 列了 5 个面板、十几项设置。**已接入的**：账本改名（L 域接口）· 成员名单 ·
 * 贡献视图（㊻）· 提醒偏好（`57`）· 主题三态（`58`）· 流水默认时间窗（`59`）·
 * 🆕 **数据主权区（`62`）**：导出流水 CSV（C 域）+ 全量备份 JSON（`61`）+ **排除表逐条明示**（`AC-N4.11`）+
 * 🆕 **从备份恢复（`65`）**：入口在本卡，向导在 `RestoreDialog`（`AC-N4.17` 宿主 + `AC-N4.19`/`N4.20` UI 强制）·
 * 🆕 **本币切换（批 `121` / K4 · `DR-068`）**：账本卡片内「本币」行 + 内联二次确认（`AC-L6.5`）。
 * **仍无接口**：智能记账开关 · 协议页 ⇒ 见页脚「尚未落地」清单。
 *
 * ⇒ 本页**不画**它们。理由是本项目一贯的「**不画灰项**」：
 * 一个点得动、但什么也不发生的开关，比没有这个开关更糟 —— 它**承诺了一件产品做不到的事**，
 * 而用户无法分辨"还没做"和"坏了"。⇒ 改为在页脚**一次性列清**「尚未落地」清单（可点不动的诚实说明）。
 *
 * ## 🔴 三条设置页专属纪律
 *
 * | # | 纪律 | 本页怎么做的 |
 * |---|---|---|
 * | ① | **每项显式标注作用域**（N 域 PRD §5.1 铁律） | 账本级项与用户级项**分组呈现** + 每项带「账本级 / 用户级」标签 —— 防"个人主题改了以为全家变了" |
 * | ② | **只读项要说清为什么只读** | 存货模式**只展示不开关**（P0 禁用露出，`AC-N2.17`）⇒ 标「P1」并写明原因。🆕 本币**已可切**（批 `121`），故不再有"暂不可改"这种说法 |
 * | ③ | **信任承诺逐条带状态** | 6 条按 N 域 PRD §5.5① 原表呈现 —— 🔴 第 3 条「导出永久免费」PRD 自己标着「须先修完整性」，本页**照实写「导出接口未落地」**，不粉饰成已具备 |
 */

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { BASE_CURRENCY_CODES, LedgerNameSchema, currencySymbolOf } from '@jiaying/shared';
import type { LedgerMemberView } from '@jiaying/shared';
import { useApp } from '../app/store.js';
import {
  dissolveLedger,
  listLedgerMembers,
  listLedgers,
  removeLedgerMember,
  renameLedger,
  transferOwnership,
  updateLedgerBaseCurrency,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { invalidateAfterBaseCurrencyChange } from '../features/invalidate.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Grid } from '../ui/Grid.js';
import { Loading } from '../ui/Loading.js';
import { ContributionCard } from '../ui/ContributionCard.js';
import { DataSovereigntyCard } from '../ui/DataSovereigntyCard.js';
import { ReminderPreferencePanel } from '../ui/ReminderPreferencePanel.js';
import { ThemePreferencePanel } from '../ui/ThemePreferencePanel.js';
import { TxnWindowPreferencePanel } from '../ui/TxnWindowPreferencePanel.js';
import { ScopeTag } from '../ui/ScopeTag.js';
import { ConfirmByNameBody } from '../ui/ConfirmByNameDialog.js';
import { InviteCard } from '../ui/InviteCard.js';
import { JoinLedgerCard } from '../ui/JoinLedgerDialog.js';
import { MemberAdminList } from '../ui/MemberAdminList.js';
import {
  DISSOLVE_CONFIRM_LABEL,
  DISSOLVE_EXPLAIN,
  DISSOLVE_TITLE,
  REMOVE_CONFIRM_LABEL,
  REMOVE_TITLE,
  MEMBERS_SECTION_TITLE,
  MEMBERS_SHARED_HINT,
  removeExplain,
  transferExplain,
  TRANSFER_TITLE,
} from '../features/collab-labels.js';

/** 🔴 N 域 PRD §5.5① 的 6 条信任承诺 —— 逐字照抄那张表，**含状态列**（不粉饰、不删项） */
const TRUST: readonly { promise: string; proof: string; ready: boolean }[] = [
  { promise: '永远没有广告', proof: '第三方 SDK = 0', ready: true },
  { promise: '不做理财导流', proof: '无基金 / 贷款 / 保险推荐入口', ready: true },
  // ✅ 服务端已于 `61` 批落地（全量备份 JSON）、端侧入口于本批（`62`）落地（C 域流水 CSV + N 域备份）⇒ 兑现点已齐
  { promise: '导出永久免费', proof: '不限次数、不留水印、不需会员', ready: true },
  { promise: '每一分钱都对得上', proof: '整数分 + 余额由腿派生', ready: true },
  { promise: '口径公开可解释', proof: '唯一支出内核 + composeNetWorth 唯一权威', ready: true },
  { promise: 'AI 不外发敏感数据', proof: 'PII 白名单逐字段', ready: true },
];

/** 🔴 精度承诺 4 行（N 域 PRD §5.5②）—— 数字是承诺的**可验证**部分 */
const PRECISION: readonly string[] = [
  '金额一律整数「分」，不存浮点',
  '投资价格精度 PRICE_SCALE = 10000',
  '汇率精度 1e-4',
  '余额由腿实时派生，不落库',
];

/** 「尚未落地」清单 —— 把不做的东西**写出来**，而不是让人以为产品没有这些需求 */
const PENDING: readonly string[] = [
  // 🆕 批 `121`：把「本币切换」移出本清单 —— 接口 + 端侧入口已落地（`DR-068`）。
  //    铁律 64：新词落地的同批必须撤掉旧的反向守卫，否则页面自相矛盾。
  '智能记账开关',
  // `63` 批把「注销账号」移出本清单（已端到端贯通）；`65` 批把「备份恢复」也移出（数据主权卡
  // 已有入口）；批 `105` 起「覆盖前自动快照 / 可回滚」同样移出 —— 同页 `DataSovereigntyCard`
  // 已能列表 / 下载 / 回滚，这一行留着就是**自相矛盾**：卡里能点，清单说没有（铁律 64）。
];

/**
 * 🔴 切币二次确认要讲清的三件事（`DR-068` §4 / 交接提示词 §4b 的原文口径）。
 *
 * **只此一份**：常驻说明（卡片里）与确认条（点了「更换」之后）渲染的是同一个数组 ——
 * 两处各抄一遍的话，将来改文案必然漏一处，而"改口径时少说了一句"正是这条功能最不能出的错。
 */
const CURRENCY_SWITCH_FACTS: readonly string[] = [
  '净资产与报表从现在起按新币折算',
  '历史曲线不会重算：保留当时的币种原貌，并在切换点标注',
  '账户余额与流水的原币金额不变（你的外币账户不会被换算）',
];

/** ① ② ③ —— 与 `CURRENCY_SWITCH_FACTS` 一一对应（长度不一致时渲染层直接少一条，视觉上看得见） */
const CIRCLED = ['①', '②', '③'] as const;

export function SettingsPage() {
  const qc = useQueryClient();
  const { session, setLedger } = useApp();

  const ledgers = useQuery({ queryKey: ['ledgers'], queryFn: listLedgers });
  // 🔴 与顶栏头像堆叠**同一个 queryKey** ⇒ 成员名单只有一份，不会两处漂移
  const members = useQuery({ queryKey: ['ledgerMembers'], queryFn: listLedgerMembers });

  const current = ledgers.data?.find((l) => l.id === session?.ledgerId);

  // ── 改名草稿 ──
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameOk, setNameOk] = useState(false);
  /**
   * 🔴 内容区引用 —— 交给 `<AnchorBar>` 做吸顶页内导航（`AC-O2.36`）。
   * 必须指向**内容区的直接容器**（本页 = 最外层 `<Stack>`），理由见 `AnchorBar.tsx` 使用纪律 §1。
   */
  const contentRef = useRef<HTMLDivElement | null>(null);

  // 🔴 切账本 / 首次拿到账本名时**同步草稿**（否则会把上一个账本的名字带过来）
  // ⚠️ 依赖只列 `[current?.id, current?.name]`：本仓 **未装 `eslint-plugin-react-hooks`**
  //（`pr-gate` 的 lint 里没有该规则），所以这条纪律靠**显式注释**承载，而不是靠 lint 兜。
  useEffect(() => {
    setNameDraft(current?.name ?? '');
    setNameError(null);
    setNameOk(false);
  }, [current?.id, current?.name]);

  const rename = useMutation({
    mutationFn: (name: string) => renameLedger(name),
    onSuccess: (updated) => {
      setNameOk(true);
      setNameError(null);
      // 🔴 账本名出现在**三处**（侧栏切换器 / 顶栏 / 会员面板）⇒ 缓存必须一起失效
      void qc.invalidateQueries({ queryKey: ['ledgers'] });
      // 会话里也存了一份名字（`session.ledgerName`）⇒ 同步它，否则侧栏会显示旧名
      setLedger(updated.id, updated.name);
    },
    onError: (e: unknown) => {
      setNameOk(false);
      setNameError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const submitName = (): void => {
    setNameOk(false);
    // 🔴 长度校验用**共享层同一个 schema**（`LedgerNameSchema`，≤30 字符）——
    //    端侧不写第二份 `length > 30`（那会和契约漂移）；服务端也会再校验一次。
    const parsed = LedgerNameSchema.safeParse(nameDraft);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? '账本名不合法');
      return;
    }
    setNameError(null);
    rename.mutate(parsed.data);
  };

  // ─────────────── 本币切换（批 121 · K4 · `DR-068`） ───────────────
  //
  // 🔴 三条口径都在服务端：现值跟随 / 报表重算 / 曲线不重算。
  //    端侧只做两件事：① 画入口并按角色禁用 ② 成功后失效缓存（`invalidateAfterBaseCurrencyChange`）。
  // 🔴 **不做任何"折算预览"或"差额计算"** —— 那需要汇率与全部账户，属服务层；
  //    端侧算一份必然与服务端口径分叉（本项目最贵的一类错）。
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [currencyDraft, setCurrencyDraft] = useState('');
  const [currencyError, setCurrencyError] = useState<string | null>(null);
  const [currencyOk, setCurrencyOk] = useState(false);

  const baseCurrencyMut = useMutation({
    mutationFn: (currency: string) => updateLedgerBaseCurrency(currency),
    onSuccess: (updated) => {
      setCurrencyOk(true);
      setCurrencyError(null);
      setCurrencyOpen(false);
      // 🔴 四个 key（比交接文档多一个 `networth-curve`）—— 理由见该 helper 的文件头
      invalidateAfterBaseCurrencyChange(qc);
      // 账本列表里也存了本币（顶栏符号 / `useBaseCurrency()`）⇒ 上面已失效，这里只同步会话侧的名字
      setLedger(updated.id, updated.name);
    },
    onError: (e: unknown) => {
      setCurrencyOk(false);
      setCurrencyError(e instanceof ApiError ? e.message : String(e));
    },
  });

  /** 打开确认条时把草稿指到"另一个"币种（避免一打开就撞上"与当前相同 ⇒ 400"） */
  const openCurrencyPicker = (): void => {
    setCurrencyDraft(BASE_CURRENCY_CODES.find((c) => c !== current?.baseCurrency) ?? '');
    setCurrencyError(null);
    setCurrencyOk(false);
    setCurrencyOpen(true);
  };

  // ─────────────── 协作管理（批 89 · L 域 §5.1 / §5.6） ───────────────
  //
  // 🔴 三条口径都在服务端：邀请码满员 409 · 移除/解散要二次输入名称（400）· 转让目标必须是成员。
  //    端侧**只做"画不画入口"与"非空才可点"**，错误一律原样展示 message（铁律 79）。
  const role = current?.role ?? '';
  const [removeTarget, setRemoveTarget] = useState<LedgerMemberView | null>(null);
  const [removeInput, setRemoveInput] = useState('');
  const [dissolveOpen, setDissolveOpen] = useState(false);
  const [dissolveInput, setDissolveInput] = useState('');
  /** 转让只弹内联确认条（服务端只要求 `toUserId`，没有二次输入） */
  const [transferTarget, setTransferTarget] = useState<LedgerMemberView | null>(null);

  const refreshMemberScoped = (): void => {
    // 成员变了 ⇒ 名单、账本列表（memberCount / role）、贡献视图都可能变
    void qc.invalidateQueries({ queryKey: ['ledgerMembers'] });
    void qc.invalidateQueries({ queryKey: ['ledgers'] });
    void qc.invalidateQueries({ queryKey: ['contribution'] });
  };

  const removeMut = useMutation({
    mutationFn: (args: { userId: string; confirmMemberName: string }) =>
      removeLedgerMember(args.userId, args.confirmMemberName),
    onSuccess: () => {
      setRemoveTarget(null);
      setRemoveInput('');
      refreshMemberScoped();
    },
  });

  const transferMut = useMutation({
    mutationFn: (toUserId: string) => transferOwnership(toUserId),
    onSuccess: () => {
      setTransferTarget(null);
      refreshMemberScoped();
    },
  });

  const dissolveMut = useMutation({
    mutationFn: (confirmLedgerName: string) => dissolveLedger(confirmLedgerName),
    onSuccess: (r) => {
      setDissolveOpen(false);
      setDissolveInput('');
      // 🔴 解散后当前账本仍在（`keptLedgerId`）—— 但角色/成员数变了，缓存必须失效
      void qc.invalidateQueries({ queryKey: ['ledgers'] });
      void qc.invalidateQueries({ queryKey: ['ledgerMembers'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      setLedger(r.keptLedgerId, current?.name ?? '');
    },
  });

  const errorText = (e: unknown): string =>
    e instanceof ApiError ? e.message : String(e);

  if (ledgers.isPending) return <Loading />;
  if (ledgers.isError) return <Alert severity="error">账本读取失败：{String(ledgers.error)}</Alert>;

  const memberList = members.data ?? [];
  const isOwner = memberList.find((m) => m.isSelf)?.role === 'owner';

  return (
    <Stack spacing={2} ref={contentRef}>
      {/* 🆕 🔴 吸顶页内导航（`AC-O2.36`）—— 必须是内容区第一个子节点；短页自动不产出 */}
      <AnchorBar contentRef={contentRef} />

      {/*
        🆕 网格化（㊱）：设置页按设计的 `.g2` 等宽双列分组 —— 左列「账本 / 成员」、右列「偏好 / 信任」。
        （不用不等宽：两列都是**卡片组**，没有主次语义。）
      */}
      <Grid cols={2} sx={{ alignItems: 'start' }}>
        {/* ═══════════ 左列：账本级 ═══════════ */}
        <Stack spacing={2}>
          <Card data-anchor="账本设置">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  账本设置
                </Typography>
                <ScopeTag scope="账本级" />
              </Stack>

              <TextField
                fullWidth
                size="small"
                label="账本名称"
                value={nameDraft}
                onChange={(e) => {
                  setNameDraft(e.target.value);
                  setNameOk(false);
                }}
                disabled={rename.isPending}
                helperText="仅所有者可改 · 最多 30 字符 · 允许同名（它是可读标识，不是主键）"
                // 🔴 非 owner 时**禁用而不是隐藏** —— 隐藏会让人以为"改不了"，禁用才说清"轮不到你"
                inputProps={{ maxLength: 30, 'data-testid': 'ledger-name-input' }}
              />
              {!isOwner && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  你不是所有者，账本名称当前不可改。
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  onClick={submitName}
                  disabled={rename.isPending || !isOwner || nameDraft === current?.name}
                >
                  保存
                </Button>
                {nameOk && (
                  <Typography variant="caption" color="success.main" data-testid="ledger-name-ok">
                    已保存
                  </Typography>
                )}
              </Stack>
              {nameError !== null && (
                <Alert severity="error" sx={{ mt: 1 }} data-testid="ledger-name-error">
                  {nameError}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/*
                🆕 本币（批 `121` / K4 · `DR-068`）—— 从"只读展示"变成**可切**。
                🔴 二次确认用**内联确认条**而不是 MUI `Dialog`：`Dialog` 走 Portal，
                静态渲染拿不到内容（铁律 121）⇒ "确认条里说了哪三件事"会变成**不可断言**的东西。
                同页「转让 / 移除 / 解散」也都是内联确认条，形态一致。
              */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  本币
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={current?.baseCurrency ?? '—'}
                  data-testid="base-currency-chip"
                />
                {/*
                  🔴 非 owner **禁用而不是隐藏** —— 与上面的账本名输入框同款：
                  隐藏会让人以为"这个功能不存在"，禁用才说清"轮不到你"。
                */}
                <Button
                  size="small"
                  onClick={openCurrencyPicker}
                  disabled={!isOwner || baseCurrencyMut.isPending}
                  data-testid="base-currency-change"
                >
                  更换
                </Button>
                {currencyOk && (
                  <Typography variant="caption" color="success.main" data-testid="base-currency-ok">
                    已切换
                  </Typography>
                )}
              </Stack>
              {!isOwner && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  你不是所有者，本币当前不可改。
                </Typography>
              )}

              {/* 常驻说明：切换前就让人知道它到底改变了什么、没改变什么 */}
              <Box component="div" sx={{ mt: 0.5 }} data-testid="base-currency-explain">
                {CURRENCY_SWITCH_FACTS.map((fact, i) => (
                  <Typography variant="caption" color="text.secondary" component="div" key={fact}>
                    {CIRCLED[i]} {fact}
                  </Typography>
                ))}
              </Box>

              {currencyOpen && (
                <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="base-currency-confirm">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    切换本币为 {currencyDraft}？
                  </Typography>
                  <TextField
                    select
                    size="small"
                    label="切换为"
                    value={currencyDraft}
                    onChange={(e) => {
                      setCurrencyDraft(e.target.value);
                      setCurrencyError(null);
                    }}
                    disabled={baseCurrencyMut.isPending}
                    fullWidth
                    data-testid="base-currency-select"
                  >
                    {/*
                      🔴 候选来自共享层 `BASE_CURRENCY_CODES`（= `CURRENCY_SYMBOLS` 的 key），
                      端侧**不写第二份币种清单**；当前本币不出现在候选里（同值必 400，`DR-068` §3）。
                      ⚠️ 不按币种码排序：保持符号表的原顺序，让"常见币种在前"这件事只由那一处决定。
                    */}
                    {BASE_CURRENCY_CODES.filter((c) => c !== current?.baseCurrency).map((c) => (
                      <MenuItem key={c} value={c}>
                        {c} {currencySymbolOf(c)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ mt: 1 }}>
                    {CURRENCY_SWITCH_FACTS.map((fact, i) => (
                      <Typography variant="caption" component="div" key={fact}>
                        {CIRCLED[i]} {fact}
                      </Typography>
                    ))}
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={
                        baseCurrencyMut.isPending ||
                        currencyDraft === '' ||
                        currencyDraft === current?.baseCurrency
                      }
                      onClick={() => {
                        baseCurrencyMut.mutate(currencyDraft);
                      }}
                      data-testid="base-currency-confirm-ok"
                    >
                      确认切换
                    </Button>
                    <Button
                      size="small"
                      disabled={baseCurrencyMut.isPending}
                      onClick={() => {
                        setCurrencyOpen(false);
                        setCurrencyError(null);
                      }}
                      data-testid="base-currency-cancel"
                    >
                      取消
                    </Button>
                  </Stack>
                  {currencyError !== null && (
                    <Alert severity="error" sx={{ mt: 1 }} data-testid="base-currency-error">
                      {currencyError}
                    </Alert>
                  )}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/*
                🔴 存货模式 —— **P0 禁用露出**（`AC-N2.17`）：
                N 域 PRD §3.1 明确「存货模式（**P0 禁用露出 + 标注 P1**）」，
                且 §3.2 把「存货模式前端消费端」列为**待单独拍板**项。
                ⇒ 画一个**明确禁用**的开关 + 「P1」标注，比整项不画更好：
                不画会让"这个产品没有存货概念"，而事实是**服务端已有、只是本轮不开放**。
              */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  存货模式
                </Typography>
                <Switch size="small" disabled checked={false} data-testid="stock-mode-switch" />
                <Chip size="small" variant="outlined" label="P1" data-testid="stock-mode-p1" />
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                存货（进货 / 出库 / 盘点）的前端消费端属 P1，且需单独拍板 —— 本轮**明确不开放**，
                而不是"忘做了"。
              </Typography>
            </CardContent>
          </Card>

          <Card data-anchor="成员">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {MEMBERS_SECTION_TITLE}
                </Typography>
                <ScopeTag scope="账本级" />
              </Stack>
              {members.isPending && <CircularProgress size={20} />}
              {members.isError && (
                <Alert severity="error">成员读取失败：{String(members.error)}</Alert>
              )}
              {members.isSuccess && (
                <MemberAdminList
                  members={memberList}
                  role={role}
                  onTransfer={(m) => {
                    setTransferTarget(m);
                    transferMut.reset();
                  }}
                  onRemove={(m) => {
                    setRemoveTarget(m);
                    setRemoveInput('');
                    removeMut.reset();
                  }}
                />
              )}

              {/* 转让 = 内联确认条（服务端只要求 `toUserId`，没有二次输入） */}
              {transferTarget !== null && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  data-testid="transfer-confirm"
                  action={
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        disabled={transferMut.isPending}
                        onClick={() => {
                          transferMut.mutate(transferTarget.userId);
                        }}
                        data-testid="transfer-confirm-ok"
                      >
                        确认转让
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setTransferTarget(null);
                        }}
                        data-testid="transfer-confirm-cancel"
                      >
                        取消
                      </Button>
                    </Stack>
                  }
                >
                  <strong>{TRANSFER_TITLE}</strong>：{transferExplain(transferTarget.name)}
                </Alert>
              )}
              {transferMut.isError && (
                <Alert severity="error" sx={{ mt: 1 }} data-testid="transfer-error">
                  {errorText(transferMut.error)}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                {MEMBERS_SHARED_HINT}
              </Typography>

              {/* 🔴 解散：仅 owner；放在成员卡底部（它作用于"这个家"） */}
              {role === 'owner' && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => {
                      setDissolveOpen(true);
                      setDissolveInput('');
                      dissolveMut.reset();
                    }}
                    data-testid="dissolve-open"
                  >
                    {DISSOLVE_TITLE}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/*
            🔴 邀请码卡（批 `89`）：`@RequireOwner(InviteMember)` ⇒ 非 owner 只看到一句解释，
            不画按钮（不画灰项）。满员 409 原样展示服务端 message。
          */}
          <InviteCard role={role} />

          {/*
            🔴 加入家庭（批 `89`）：预览四栏 + 策略二选一（`AC-L5.1` / `L5.4`）。
            成功后刷新账本列表并**切换账本上下文** —— 否则"加入了还看着旧账本"。
          */}
          <JoinLedgerCard
            onJoined={(r) => {
              void qc.invalidateQueries({ queryKey: ['ledgers'] });
              void qc.invalidateQueries({ queryKey: ['ledgerMembers'] });
              void qc.invalidateQueries({ queryKey: ['networth'] });
              setLedger(r.ledgerId, '家庭账本');
            }}
          />

          {/*
            🔴 **贡献视图**（L 域 L3 · `AC-L3.7` / `L3.9` / `L3.10`）——
            与成员卡**同列、紧随其后**：两者都是"这个家的协作状态"，拆到两列会读成两件事。

            ⚠️ 单人账本时它自己换成**邀请引导卡**（`singleMember` 由服务端判）——
            故本页这里**不做任何分支**（分支在组件内，且判据只有一个来源）。
          */}
          <ContributionCard />

          {/*
            🆕 **N4 数据主权区**（本批 `62`）—— **导出侧**已落地：C 域流水 CSV + N 域全量备份 JSON
            （服务端在 `61` 批）。🔴 它是"信得过"的实体化：两个导出入口 + **排除表逐条明示**（`AC-N4.11`）。
            ✅ **`65` 批起该卡三件事齐了**：导出（`62`）· **从备份恢复**（`65`，向导在 `RestoreDialog`）·
            删除账户（`63`）。✅ **`68` 批起第四件也齐了**：覆盖前自动快照 / 可回滚（`AC-N4.21` ·
            `DR-039`，服务端 `67` 三条路由 + 端侧 `SnapshotSection` / `RollbackConfirmDialog`）。
            🔴 批 `105`：本条注释此前写"仍未做…⇒ 留在页脚「尚未落地」清单里"，那句就是清单那一行
            第三次复发的来源 —— 现在两句一起撤（铁律 64：新词落地则旧反向守卫同批撤）。
          */}
          <DataSovereigntyCard />
        </Stack>

        {/* ═══════════ 右列：用户级 + 信任 ═══════════ */}
        <Stack spacing={2}>
          {/*
            🆕 N 域批：**提醒偏好**（**用户级** · `DR-031` 唯一载体 = `user_preferences`）。
            🔴 它同时兑现了 `56` 批提醒中心页写的那句指路（「改强度…都在设置里」）。
          */}
          <ReminderPreferencePanel />

          {/*
            🆕 N 域批（时间窗）：**流水默认时间窗** —— `AC-N2.18`（1/3/6 月，默认近 3 月）。
            🔴 它是「用户级三项」里最后接上写路径的一项（主题 / 提醒偏好 / 时间窗）。
            消费方 = C 域流水页（`baseWindowOf(txnWindow)` ⇒ 基线窗口）。
          */}
          <TxnWindowPreferencePanel />

          {/*
            🆕 N 域批（主题三态）：**「外观」卡** —— 三态 segmented + 品牌色只读展示。
            🔴 本批之前这里是一个**二态切换按钮**（`themeMode` 只有 light/dark，装不下「跟随系统」）
            ⇒ 现在改由 `ThemePreferencePanel` 承担（三态 + 服务端持久化，`AC-N3.1`/`N3.2`/`N3.3`）。
          */}
          <ThemePreferencePanel />

          <Card data-anchor="信任与合规">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                信任与合规
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, mb: 1 }}>
                每条承诺都绑定一个兑现点 —— 没有兑现点的承诺不上线。
              </Typography>
              <List dense disablePadding>
                {TRUST.map((t) => (
                  <ListItem key={t.promise} disableGutters alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>{t.promise}</span>
                          {/* 🔴 状态如实呈现：未就绪的写「未就绪」，不粉饰 */}
                          <Chip
                            size="small"
                            label={t.ready ? '已具备' : '未就绪'}
                            color={t.ready ? 'success' : 'default'}
                            variant={t.ready ? 'outlined' : 'filled'}
                          />
                        </Stack>
                      }
                      secondary={t.proof}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                精度承诺（可验证，不是宣传语）
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {PRECISION.map((p) => (
                  <li key={p}>
                    <Typography variant="caption" color="text.secondary">
                      {p}
                    </Typography>
                  </li>
                ))}
              </Box>

              {/*
                🆕 批 `90`：协议与隐私政策的入口（N5）。
                🔴 它们此前在页脚「尚未落地」清单里挂着 —— 有页面了就必须同批移出（铁律 64 的同源纪律：
                新旧陈述不能并存，否则页面说"有"、清单说"没有"）。
              */}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={2} data-testid="legal-links">
                <Typography
                  variant="caption"
                  component={RouterLink}
                  to="/terms"
                  data-testid="link-terms"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  用户协议
                </Typography>
                <Typography
                  variant="caption"
                  component={RouterLink}
                  to="/privacy"
                  data-testid="link-privacy"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  隐私政策
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      {/* 🔴 「尚未落地」一次列清 —— 比让人以为产品没这些需求要好 */}
      <Alert severity="info" icon={false} data-testid="settings-pending">
        <Typography variant="caption" component="div">
          <strong>这些设置项还没有接口，因此本页不画</strong>（点不动的开关比没有更糟）：
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {PENDING.map((p) => (
            <li key={p}>
              <Typography variant="caption">{p}</Typography>
            </li>
          ))}
        </Box>
      </Alert>

      {/*
        ── 不可逆动作的二次确认（L 域 §5.5.5）──
        🔴 两个对话框共用 `ConfirmByNameBody`：内容层零 Portal、纯受控 ⇒ 可被静态断言。
        🔴 "输入必须与目标名称相同"由**服务端**校验（400），端侧只判非空 —— 见 `collab.ts` 文件头。
      */}
      <Dialog
        open={removeTarget !== null}
        onClose={() => {
          if (!removeMut.isPending) {
            setRemoveTarget(null);
            setRemoveInput('');
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 2.5 }}>
          {removeTarget !== null && (
            <ConfirmByNameBody
              title={REMOVE_TITLE}
              description={removeExplain(removeTarget.name)}
              inputLabel={REMOVE_CONFIRM_LABEL}
              expectedHint={removeTarget.name}
              value={removeInput}
              onChange={setRemoveInput}
              busy={removeMut.isPending}
              error={removeMut.isError ? errorText(removeMut.error) : null}
              confirmLabel="移除"
              onCancel={() => {
                setRemoveTarget(null);
                setRemoveInput('');
              }}
              onConfirm={() => {
                removeMut.mutate({
                  userId: removeTarget.userId,
                  confirmMemberName: removeInput.trim(),
                });
              }}
              testId="remove-member"
            />
          )}
        </Box>
      </Dialog>

      <Dialog
        open={dissolveOpen}
        onClose={() => {
          if (!dissolveMut.isPending) setDissolveOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 2.5 }}>
          <ConfirmByNameBody
            title={DISSOLVE_TITLE}
            description={DISSOLVE_EXPLAIN}
            inputLabel={DISSOLVE_CONFIRM_LABEL}
            expectedHint={current?.name ?? ''}
            value={dissolveInput}
            onChange={setDissolveInput}
            busy={dissolveMut.isPending}
            error={dissolveMut.isError ? errorText(dissolveMut.error) : null}
            confirmLabel="解散"
            onCancel={() => {
              setDissolveOpen(false);
              setDissolveInput('');
            }}
            onConfirm={() => {
              dissolveMut.mutate(dissolveInput.trim());
            }}
            testId="dissolve-ledger"
          />
        </Box>
      </Dialog>
    </Stack>
  );
}
